import React, { useEffect } from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails } from '../core/_models'

interface AvailabilityTabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  selectedLanguage: string
}

const AvailabilityTab: React.FC<AvailabilityTabProps> = ({ formik, intl, selectedLanguage }) => {
  useEffect(() => {
    if (formik.values.enableFixedDate === undefined || formik.values.enableFixedDate === false) {
      formik.setFieldValue('enableFixedDate', true)
    }
  }, [])

  const lang = selectedLanguage === 'en' || selectedLanguage === 'de' || selectedLanguage === 'fr'
    ? selectedLanguage
    : 'fr'

  return (
    <div className='d-flex flex-column gap-7 gap-lg-10'>
      <div className='card card-flush shadow-sm mb-2'>
        <div className='card-body d-flex flex-column flex-md-row align-items-md-center justify-content-md-between py-4 gap-4'>
          <div>
            <h5 className='mb-1 fw-bold'>
              {intl.formatMessage({ id: 'TOUR.AVAILABILITY.ONLINE_BOOKING_TITLE' })}
            </h5>
            <span className='text-gray-500 fs-7'>
              {intl.formatMessage({ id: 'TOUR.AVAILABILITY.ONLINE_BOOKING_HELP' })}
            </span>
          </div>
          <div className='form-check form-switch form-switch-lg flex-shrink-0'>
            <input
              className='form-check-input'
              type='checkbox'
              id='tour-is-bookable'
              checked={formik.values.isBookable !== false}
              onChange={(e) => formik.setFieldValue('isBookable', e.target.checked)}
            />
          </div>
        </div>
        {formik.values.isBookable === false && (
          <div className='card-body pt-0 border-top'>
            <label className='form-label fw-semibold'>
              {intl.formatMessage(
                { id: 'TOUR.AVAILABILITY.NOT_BOOKABLE_MESSAGE_LABEL' },
                { lang: lang.toUpperCase() },
              )}
            </label>
            <textarea
              className='form-control form-control-solid'
              rows={2}
              placeholder={intl.formatMessage({
                id: 'TOUR.AVAILABILITY.NOT_BOOKABLE_MESSAGE_PLACEHOLDER',
              })}
              value={formik.values.notBookableMessage?.[lang] || ''}
              onChange={(e) =>
                formik.setFieldValue(`notBookableMessage.${lang}`, e.target.value)
              }
            />
          </div>
        )}
      </div>

      <div className='card card-flush py-4'>
        <div className='card-header'>
          <div className='card-title'>
            <h2>{intl.formatMessage({ id: 'TOUR.FIELDS.AVAILABILITY' })}</h2>
          </div>
        </div>
        <div className='card-body pt-0'>
          <div className='mb-10'>
            <h3 className='fs-4 fw-bold mb-4'>
              {intl.formatMessage({ id: 'TOUR.AVAILABILITY.BOOKING_BLACKOUTS' })}
            </h3>
            <p className='text-muted fs-7 mb-4'>
              {intl.formatMessage({ id: 'TOUR.AVAILABILITY.BOOKING_BLACKOUTS_HELP' })}
            </p>
            {(formik.values.bookingBlackoutRanges || []).map((row, index) => (
              <div className='row g-4 align-items-end mb-3' key={index}>
                <div className='col-md-3'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.AVAILABILITY.START_DATE' })}
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    value={row.from || ''}
                    onChange={(e) => {
                      const next = [...(formik.values.bookingBlackoutRanges || [])]
                      next[index] = { ...next[index], from: e.target.value }
                      formik.setFieldValue('bookingBlackoutRanges', next)
                    }}
                  />
                </div>
                <div className='col-md-3'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.AVAILABILITY.END_DATE' })}
                  </label>
                  <input
                    type='date'
                    className='form-control'
                    value={row.to || ''}
                    onChange={(e) => {
                      const next = [...(formik.values.bookingBlackoutRanges || [])]
                      next[index] = { ...next[index], to: e.target.value }
                      formik.setFieldValue('bookingBlackoutRanges', next)
                    }}
                  />
                </div>
                <div className='col-md-3'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.AVAILABILITY.BLACKOUT_TYPE' })}
                  </label>
                  <select
                    className='form-select'
                    value={row.type === 'custom_request' ? 'custom_request' : 'blocked'}
                    onChange={(e) => {
                      const next = [...(formik.values.bookingBlackoutRanges || [])]
                      next[index] = { ...next[index], type: e.target.value as 'blocked' | 'custom_request' }
                      formik.setFieldValue('bookingBlackoutRanges', next)
                    }}
                  >
                    <option value='blocked'>
                      {intl.formatMessage({ id: 'TOUR.AVAILABILITY.TYPE_BLOCKED' })}
                    </option>
                    <option value='custom_request'>
                      {intl.formatMessage({ id: 'TOUR.AVAILABILITY.TYPE_CUSTOM_REQUEST' })}
                    </option>
                  </select>
                </div>
                <div className='col-md-2'>
                  <button
                    type='button'
                    className='btn btn-light-danger btn-sm'
                    onClick={() => {
                      const next = (formik.values.bookingBlackoutRanges || []).filter((_, i) => i !== index)
                      formik.setFieldValue('bookingBlackoutRanges', next)
                    }}
                  >
                    {intl.formatMessage({ id: 'COMMON.REMOVE' })}
                  </button>
                </div>
                <div className='col-12'>
                  <label className='form-label'>
                    {intl.formatMessage({ id: 'TOUR.AVAILABILITY.BLACKOUT_MESSAGE' })}
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    value={row.message || ''}
                    onChange={(e) => {
                      const next = [...(formik.values.bookingBlackoutRanges || [])]
                      next[index] = { ...next[index], message: e.target.value }
                      formik.setFieldValue('bookingBlackoutRanges', next)
                    }}
                  />
                </div>
              </div>
            ))}
            <button
              type='button'
              className='btn btn-light-primary btn-sm mb-8'
              onClick={() =>
                formik.setFieldValue('bookingBlackoutRanges', [
                  ...(formik.values.bookingBlackoutRanges || []),
                  { from: '', to: '', type: 'blocked', message: '' },
                ])
              }
            >
              {intl.formatMessage({ id: 'TOUR.AVAILABILITY.ADD_BLACKOUT_RANGE' })}
            </button>

            <div className='mb-8'>
              <label className='form-label'>
                {intl.formatMessage({ id: 'TOUR.AVAILABILITY.INVALID_DATE_MESSAGE' })}
              </label>
              <textarea
                className='form-control'
                name='invalidBookingDateMessage'
                rows={3}
                value={formik.values.invalidBookingDateMessage || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={intl.formatMessage({
                  id: 'TOUR.AVAILABILITY.INVALID_DATE_MESSAGE_PLACEHOLDER',
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { AvailabilityTab }
